import { useSyncExternalStore, useMemo } from 'react';
import { DEFAULT_CONCIERGE_SETTINGS } from './types.js';

/**
 * Hook to read window.brandclubSettings.concierge with reactivity
 * Re-renders when settings change (via custom event)
 */
function useConciergeSettings() {
    // Use useSyncExternalStore for SSR-safe access to window
    const conciergeSettings = useSyncExternalStore(
    // Subscribe function - listen for changes
    (callback) => {
        const handler = () => callback();
        window.addEventListener('brandclub:settings:update', handler);
        return () => window.removeEventListener('brandclub:settings:update', handler);
    }, 
    // Get snapshot - read current settings
    () => {
        var _a;
        if (typeof window === 'undefined')
            return null;
        return (_a = window.brandclubSettings) === null || _a === void 0 ? void 0 : _a.concierge;
    }, 
    // Get server snapshot - return null for SSR
    () => null);
    // Merge with defaults
    return useMemo(() => (Object.assign(Object.assign({}, DEFAULT_CONCIERGE_SETTINGS), conciergeSettings)), [conciergeSettings]);
}
/**
 * Get Concierge settings synchronously (non-reactive)
 * Use this in callbacks or effects where you need the current value
 */
function getConciergeSettings() {
    var _a;
    if (typeof window === 'undefined') {
        return DEFAULT_CONCIERGE_SETTINGS;
    }
    return Object.assign(Object.assign({}, DEFAULT_CONCIERGE_SETTINGS), (_a = window.brandclubSettings) === null || _a === void 0 ? void 0 : _a.concierge);
}
/**
 * Update Concierge settings programmatically
 *
 * @example
 * updateConciergeSettings({ z_index: 9999 });
 */
function updateConciergeSettings(newSettings) {
    var _a;
    if (typeof window === 'undefined')
        return;
    window.brandclubSettings = Object.assign(Object.assign({}, window.brandclubSettings), { concierge: Object.assign(Object.assign({}, (_a = window.brandclubSettings) === null || _a === void 0 ? void 0 : _a.concierge), newSettings) });
    // Dispatch event to trigger re-renders
    window.dispatchEvent(new CustomEvent('brandclub:settings:update'));
}

export { getConciergeSettings, updateConciergeSettings, useConciergeSettings };
//# sourceMappingURL=useBrandclubSettings.js.map
